import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { HdBodygraphPlanetaryActivation } from '../../hd-bodygraph.models';
import { HdChartSideMode } from '../../chart-tabs/hd-chart-shortcut-bar.component';
import { HdPlanetActivationBlockClickEvent } from '../hd-planet-activation-block/hd-planet-activation-block.component';
import { HD_PLANET_DISPLAY_CONFIGS, HdPlanetDisplayConfig } from '../hd-planet-display-config';
import { findActivationFromList } from '../hd-planet-grid.utils';
import {
  HdJustNowBaseFixingPresentation,
  HdJustNowLineDataResolver,
  resolveHdJustNowBaseFixingPresentation,
} from '../hd-just-now-fixing.utils';
import { HdJustNowPlanetActivationCellComponent } from '../hd-just-now-planet-activation-cell/hd-just-now-planet-activation-cell.component';
import { HdSimpleExtraPlanetVm } from '../hd-simple-planet-grid/hd-simple-planet-grid.component';

export interface HdJustNowBasePlanetGridLabels {
  readonly title: string;
  readonly exaltation: string;
  readonly detriment: string;
  readonly sameGate: string;
  readonly harmonicGate: string;
}

interface HdJustNowBasePlanetRowVm {
  readonly displayConfig: HdPlanetDisplayConfig;
  readonly designActivation: HdBodygraphPlanetaryActivation | null;
  readonly personalityActivation: HdBodygraphPlanetaryActivation | null;
  readonly designFixing: HdJustNowBaseFixingPresentation | null;
  readonly personalityFixing: HdJustNowBaseFixingPresentation | null;
}

@Component({
  selector: 'hd-just-now-base-planet-grid',
  templateUrl: './hd-just-now-base-planet-grid.component.html',
  styleUrl: './hd-just-now-base-planet-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdJustNowPlanetActivationCellComponent],
})
export class HdJustNowBasePlanetGridComponent {
  readonly activations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly transitActivations = input.required<readonly HdBodygraphPlanetaryActivation[]>();
  readonly sideMode = input.required<HdChartSideMode>();
  readonly lineData = input.required<HdJustNowLineDataResolver>();
  readonly planetName = input.required<(planetId: number) => string>();
  readonly labels = input.required<HdJustNowBasePlanetGridLabels>();
  /** Optionele titelkleur — de cycle-kant van een cycle chart kleurt de titel groen. */
  readonly titleAccentColor = input<string | null>(null);

  /** Chartnaam boven de D|P-titel (composite: wie deze kant is); null = geen naamregel. */
  readonly chartLabel = input<string | null>(null);
  /** Optioneel informatief Chiron-item per zijde (geen activatie; gedimde rij onderaan). */
  readonly designChiron = input<HdSimpleExtraPlanetVm | null>(null);
  readonly personalityChiron = input<HdSimpleExtraPlanetVm | null>(null);
  readonly contextMenuEnabled = input(true);

  readonly planetActivationClicked = output<HdPlanetActivationBlockClickEvent>();
  readonly planetBlockContextMenuRequested = output<{ gate: number; position: { x: number; y: number } }>();

  protected readonly rows = computed<readonly HdJustNowBasePlanetRowVm[]>(() => {
    const activations = this.activations();
    const transitActivations = this.transitActivations();
    const sideMode = this.sideMode();
    const lineData = this.lineData();
    return HD_PLANET_DISPLAY_CONFIGS.map(displayConfig => {
      const designActivation = sideMode === 'personality'
        ? null
        : findActivationFromList(activations, 'design', displayConfig.planetId) ?? null;
      const personalityActivation = sideMode === 'design'
        ? null
        : findActivationFromList(activations, 'personality', displayConfig.planetId) ?? null;
      return {
        displayConfig,
        designActivation,
        personalityActivation,
        designFixing: designActivation
          ? resolveHdJustNowBaseFixingPresentation(designActivation, transitActivations, lineData)
          : null,
        personalityFixing: personalityActivation
          ? resolveHdJustNowBaseFixingPresentation(personalityActivation, transitActivations, lineData)
          : null,
      };
    });
  });

  protected transitTooltip(presentation: HdJustNowBaseFixingPresentation | null): string {
    if (presentation == null || presentation.transitCauses.length === 0) {
      return '';
    }
    const labels = this.labels();
    return presentation.transitCauses.map(cause => {
      const fixingLabel = cause.fixing === 'exaltation' ? labels.exaltation : labels.detriment;
      const relationLabel = cause.relation === 'same-gate' ? labels.sameGate : labels.harmonicGate;
      return `${this.planetName()(cause.planetId)}: ${fixingLabel} (${relationLabel})`;
    }).join('\n');
  }
}
